import { FC, useContext, useMemo } from 'react';
import { generatePath } from 'react-router';
import { Box } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import {
  ApplicationsSearchParams,
  useGetApplicationsQuery,
} from '~/core/shared/api/middle-office/application';
import { useTableParams } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Sorting } from '~/core/shared/types';
import { createFilterParams } from '~/core/shared/utils/queries';
import { TenantDocumentObjectDefinition } from '../../config';
import { MIDDLE_OFFICE_ROUTES } from '../../routing/routes';
import { DocumentListContext, DocumentListViewContext } from '../application';
import { ReactQueryTable } from './ReactQueryTable.component';

type DocumentsTableProps<D extends TenantDocumentObjectDefinition> = {
  columns: ColumnDef<D>[];
  defaultSorting: Sorting;
  rowPath?: string;
};

export const DocumentsTable: FC<
  DocumentsTableProps<TenantDocumentObjectDefinition>
> = ({ columns, rowPath, defaultSorting }) => {
  const { filters, setSorting } = useContext(DocumentListContext);
  const { config } = useContext(DocumentListViewContext);
  const definition =
    config.documentRouteProps?.tenantDocumentDefinition || 'unknownDefinition';

  const additionalParams = useMemo(
    () => createFilterParams(filters),
    [filters],
  );

  const { tableProps, searchParams } = useTableParams(
    defaultSorting,
    undefined,
    additionalParams,
  );

  const {
    applications,
    meta: applicationsMeta,
    ...applicationsQueryState
  } = useGetApplicationsQuery(
    searchParams as ApplicationsSearchParams,
    definition,
  );

  const getApplicationPath = useEventHandler(
    (application: TenantDocumentObjectDefinition): string =>
      generatePath(rowPath ?? MIDDLE_OFFICE_ROUTES.application.path, {
        uuid: application.id,
      }),
  );

  const onSortingChange = useEventHandler((sorting: Sorting): void => {
    tableProps.onSortingChange(sorting);
    setSorting(sorting);
  });

  return (
    <Box bg="white">
      <ReactQueryTable<TenantDocumentObjectDefinition>
        columns={columns}
        data={applications}
        isLoading={applicationsQueryState.isFetching}
        meta={applicationsMeta}
        rowPath={getApplicationPath}
        {...tableProps}
        onSortingChange={onSortingChange}
      />
    </Box>
  );
};
